import React, { useEffect, useMemo, useState } from 'react';
import { Edit, Plus, Search, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import useAccountStore from '../../store/useAccountStore';
import Pagination from '../../components/common/Pagination';
import { useConfirmation } from '../../components/providers/ConfirmationProvider';
import { getEmploymentTypeLabel, getPersonnelGroupLabel } from '../../utils/accountLabels';

const employmentTabs = [
  { id: 'all', label: 'Tümü' },
  { id: 'FIXED', label: 'Sabit' },
  { id: 'DAILY', label: 'Yevmiyeli' }
];

const statusFilterOptions = [
  { value: 'Active', label: 'Aktif Personeller' },
  { value: 'Passive', label: 'Pasif Personeller' },
  { value: 'all', label: 'Tüm Personeller' }
];

const PersonnelManagement = () => {
  const navigate = useNavigate();
  const {
    accounts,
    pagination,
    loading,
    fetchAccounts,
    deleteAccount
  } = useAccountStore();
  const { confirm } = useConfirmation();

  const [searchTerm, setSearchTerm] = useState('');
  const [employmentType, setEmploymentType] = useState('all');
  const [statusFilter, setStatusFilter] = useState('Active');

  const query = useMemo(() => ({
    search: searchTerm,
    type: 'EMPLOYEE',
    employmentType: employmentType === 'all' ? undefined : employmentType,
    status: statusFilter === 'all' ? undefined : statusFilter,
    viewType: 'all',
    page: 1,
    limit: 10
  }), [employmentType, searchTerm, statusFilter]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      fetchAccounts(query);
    }, 250);

    return () => window.clearTimeout(timer);
  }, [fetchAccounts, query]);

  const personnel = useMemo(() => (
    Array.isArray(accounts) ? accounts : []
  ), [accounts]);

  const handleDelete = async (event, personnelAccount) => {
    event.stopPropagation();
    const confirmed = await confirm(`${personnelAccount.name} personel kaydını silmek istediğinize emin misiniz?`);
    if (!confirmed) return;

    const result = await deleteAccount(personnelAccount._id);
    if (result.success) {
      toast.success('Personel silindi');
      fetchAccounts(query);
    } else {
      toast.error(result.message || 'Personel silinemedi', { duration: 6000 });
    }
  };

  const handleRowClick = (personnelAccount) => {
    navigate(`/personnel/${personnelAccount._id}`);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-slate-50/50 p-4 rounded-4xl border border-slate-100">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">Personel Yönetimi</h1>
          <p className="text-slate-500 text-sm">Personel carileri Account koleksiyonu üzerinden yönetilir</p>
        </div>
        <div className="flex w-full flex-1 flex-col gap-3 md:flex-row md:items-center md:justify-end">
          <div className="relative w-full max-w-xs">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              placeholder="Personel ara..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="w-full pl-11 pr-4 py-3 rounded-2xl border border-transparent bg-white shadow-sm focus:ring-4 focus:ring-blue-500/10 outline-none transition-all text-sm font-bold"
            />
          </div>
          <button
            type="button"
            onClick={() => navigate('/personnel/new')}
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-black text-white px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all"
          >
            <Plus size={18} /> Yeni Personel Ekle
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2 p-1 bg-white w-fit rounded-2xl border border-slate-100 shadow-sm">
          {employmentTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setEmploymentType(tab.id)}
              className={`px-5 py-2 rounded-xl text-xs font-black transition-all ${employmentType === tab.id ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 p-1 bg-white w-fit rounded-2xl border border-slate-100 shadow-sm">
          {statusFilterOptions.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setStatusFilter(tab.value)}
              className={`px-5 py-2 rounded-xl text-xs font-black transition-all ${statusFilter === tab.value ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'}`}
            >
              {tab.label.replace(' Personeller', '')}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden relative">
        {loading && (
          <div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] z-10 flex items-center justify-center font-bold text-slate-400">
            Güncelleniyor...
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-50">
                <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Personel</th>
                <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Tip</th>
                <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">İletişim</th>
                <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {personnel.map((personnelAccount) => {
                return (
                  <tr
                    key={personnelAccount._id}
                    className="cursor-pointer transition-colors hover:bg-slate-50/80"
                    onClick={() => handleRowClick(personnelAccount)}
                  >
                    <td className="p-6">
                      <p className="font-black text-slate-800">{personnelAccount.name}</p>
                      <p className="text-[10px] font-bold text-slate-400 uppercase">{personnelAccount.code || 'Kodsuz'}</p>
                    </td>
                    <td className="p-6">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="rounded-xl bg-blue-50 px-3 py-1.5 text-[10px] font-black text-blue-700">
                          {getEmploymentTypeLabel(personnelAccount.employmentType || 'FIXED')}
                        </span>
                        {(personnelAccount.employmentType || 'FIXED') === 'FIXED' && getPersonnelGroupLabel(personnelAccount.personnelGroup) ? (
                          <span className="rounded-xl border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-black text-slate-500">
                            {getPersonnelGroupLabel(personnelAccount.personnelGroup)}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="p-6 text-sm font-bold text-slate-600">
                      {personnelAccount.phone || personnelAccount.email || '-'}
                    </td>
                    <td className="p-6 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={(event) => { event.stopPropagation(); navigate(`/personnel/${personnelAccount._id}`); }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-xl"
                          title="Düzenle"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          type="button"
                          onClick={(event) => handleDelete(event, personnelAccount)}
                          className="p-2 text-rose-500 hover:bg-rose-50 rounded-xl"
                          title="Sil"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {personnel.length === 0 && (
                <tr>
                  <td colSpan="4" className="py-16 text-center text-slate-400 font-bold">
                    Personel kaydı bulunamadı.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={(page) => fetchAccounts({ ...query, page })}
        />
      </div>
    </div>
  );
};

export default PersonnelManagement;
